import { type Property, type User } from '@kalos/kalos-rpc';
import { Badge, Button, Dialog, DialogContent } from '@kalos/ui';
import { AlertTriangle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useContractQuery } from '../../../../hooks/react-query/useContractsQuery';
import { useInvoiceQuery } from '../../../../hooks/react-query/useInvoiceQuery';
import { usePropertiesBatchGetQuery } from '../../../../hooks/react-query/usePropertiesQuery';
import { CONTRACT_BADGE_CLASS, contractBadgeStatus } from '../../../../tools/contractStatus';
import { formatDate } from '../../../../tools/helpers';
import { MembershipAddonsList } from '../../../Memberships/components/MembershipAddonsList';
import { usePropertyMembership } from '../../../Memberships/hooks/usePropertyMembership';
import { EditContractInfo } from '../../EditContractInfo';

interface CustomerPropertyContractGridProps {
  customer: User;
  property: Property;
  onChangeCustomer?: () => void;
  onChangeProperty?: () => void;
  existingEvent?: boolean;
  onShowCustomerJobs?: () => void;
}

export const CustomerPropertyContractGrid = ({
  customer,
  property,
  onChangeCustomer,
  onChangeProperty,
  existingEvent,
  onShowCustomerJobs,
}: CustomerPropertyContractGridProps) => {
  const [contractExpanded, setContractExpanded] = useState(false);
  const [showEditContract, setShowEditContract] = useState(false);

  const contractQuery = useContractQuery({
    filter: {
      userId: customer.id,
      isActive: 1,
      orderBy: 'contract_date_created',
      orderDir: 'desc',
    },
    enabled: !!customer.id,
  });
  const contract = contractQuery.data?.id ? contractQuery.data : null;

  // Fetch invoice data for the contract (amount + services)
  const invoiceQuery = useInvoiceQuery({
    filter: { contractId: contract?.id ?? 0 },
    enabled: !!contract?.id,
  });

  // This property's plan, for its add-ons in the expanded view
  const propertyMembership = usePropertyMembership({
    propertyId: property.id,
    enabled: contractExpanded && !!contract,
  }).data?.membership;

  // Fetch properties included in the contract (for the expanded view)
  const contractPropertyIds = useMemo(() => {
    if (!contract?.properties) return [];
    return contract.properties
      .split(',')
      .map((id) => parseInt(id.trim()))
      .filter((id) => !isNaN(id));
  }, [contract?.properties]);

  const propertyOnContract = contract ? contractPropertyIds.includes(property.id) : false;

  // Only fetch if the contract covers properties beyond the current one
  const hasOtherProperties =
    contractPropertyIds.length > 1 ||
    (contractPropertyIds.length === 1 && contractPropertyIds[0] !== property.id);

  const contractPropertiesQuery = usePropertiesBatchGetQuery({
    filter: { userId: customer.id },
    enabled: contractExpanded && hasOtherProperties,
  });

  const contractProperties = useMemo(() => {
    if (!contractPropertyIds.length) return [];
    // If the only property in the contract is the current one, use it directly
    if (!hasOtherProperties) return contractPropertyIds.includes(property.id) ? [property] : [];
    if (!contractPropertiesQuery.data?.results?.length) return [];
    return contractPropertiesQuery.data.results.filter((p) => contractPropertyIds.includes(p.id));
  }, [contractPropertiesQuery.data?.results, contractPropertyIds, hasOtherProperties, property]);

  // Derive contract status
  const contractStatus = contract ? contractBadgeStatus(contract) : null;

  const statusColor = contractStatus ? CONTRACT_BADGE_CLASS[contractStatus] : '';

  // Contract amount from invoice
  const contractAmount = useMemo(() => {
    if (!contract) return null;
    if (invoiceQuery.isLoading) return 'Loading...';
    if (invoiceQuery.data?.totalamounttotal) {
      const amount = parseFloat(invoiceQuery.data.totalamounttotal);
      if (!isNaN(amount)) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
          amount,
        );
      }
    }
    return null;
  }, [contract, invoiceQuery.data, invoiceQuery.isLoading]);

  // Services from invoice
  const servicesIncluded = useMemo(() => {
    if (!invoiceQuery.data) return [];
    const services: string[] = [];
    if (invoiceQuery.data.servicesperformedrow1)
      services.push(invoiceQuery.data.servicesperformedrow1);
    if (invoiceQuery.data.servicesperformedrow2)
      services.push(invoiceQuery.data.servicesperformedrow2);
    if (invoiceQuery.data.servicesperformedrow3)
      services.push(invoiceQuery.data.servicesperformedrow3);
    if (invoiceQuery.data.servicesperformedrow4)
      services.push(invoiceQuery.data.servicesperformedrow4);
    return services;
  }, [invoiceQuery.data]);

  // First service name for the compact summary
  const planName = servicesIncluded[0] || null;

  const contractPeriod = contract
    ? `${contract.dateStarted ? formatDate(contract.dateStarted) : ''} \u2013 ${contract.dateEnded ? formatDate(contract.dateEnded) : ''}`
    : '';

  const customerName = `${customer.firstname ?? ''} ${customer.lastname ?? ''}`.trim();
  const customerPhone = customer.phone || customer.cellphone || customer.altphone || '';
  const propertyAddress = property.address || '';
  const propertyCityStateZip = [property.city, property.state, property.zip]
    .filter(Boolean)
    .join(', ')
    .replace(/, (?=\d)/, ' ');

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* 3-column grid */}
      <div className="grid grid-cols-1 divide-y divide-gray-200 p-5 md:grid-cols-3 md:divide-x md:divide-y-0">
        {/* CUSTOMER column */}
        <div className="space-y-1 pb-4 md:pb-0 md:pr-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Customer</p>
          <p className="font-semibold text-gray-900">{customerName}</p>
          {customerPhone && (
            <p>
              <a
                href={`tel:${customerPhone}`}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                {customerPhone}
              </a>
            </p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <a
              href={`/customers/${customer.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
            >
              View customer <ExternalLink className="h-3 w-3" />
            </a>
            {onShowCustomerJobs && (
              <button
                type="button"
                onClick={onShowCustomerJobs}
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                View jobs <ExternalLink className="h-3 w-3" />
              </button>
            )}
            {!existingEvent && onChangeCustomer && (
              <Button variant="outline" size="sm" type="button" onClick={onChangeCustomer}>
                Change
              </Button>
            )}
          </div>
        </div>

        {/* PROPERTY column */}
        <div className="space-y-1 py-4 md:px-5 md:py-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Property</p>
          <p className="font-semibold text-gray-900">{propertyAddress}</p>
          <p className="text-sm text-gray-600">{propertyCityStateZip}</p>
          <div className="flex flex-wrap gap-2 pt-1">
            <a
              href={`/customers/${customer.id}/properties/${property.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
            >
              View property <ExternalLink className="h-3 w-3" />
            </a>
            {!existingEvent && onChangeProperty && (
              <Button variant="outline" size="sm" type="button" onClick={onChangeProperty}>
                Change
              </Button>
            )}
          </div>
        </div>

        {/* CONTRACT column */}
        <div className="space-y-1 pt-4 md:pl-5 md:pt-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contract</p>
          {contractQuery.isLoading ? (
            <p className="text-sm text-gray-400">Loading...</p>
          ) : contract ? (
            <>
              <div className="flex items-center gap-2">
                <Badge
                  className={`${statusColor} pointer-events-none rounded-full px-2 py-0.5 text-xs font-medium`}
                >
                  {contractStatus}
                </Badge>
                <span className="font-semibold text-gray-900">
                  {contract.number || `C${contract.id}`}
                </span>
              </div>
              {planName && <p className="text-sm text-gray-600">{planName}</p>}
              <p className="text-sm text-gray-600">
                {contract.dateEnded && `Expires ${formatDate(contract.dateEnded)}`}
                {contractAmount && ` · ${contractAmount} due onsite`}
              </p>
              {!propertyOnContract && (
                <div className="flex items-center gap-1 text-sm text-amber-600">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Property not on contract
                </div>
              )}
              <button
                type="button"
                onClick={() => setContractExpanded(!contractExpanded)}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                View contract details
                {contractExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
            </>
          ) : (
            <p className="text-sm text-gray-500">No contract</p>
          )}
        </div>
      </div>

      {/* Expandable contract details */}
      {contract && contractExpanded && (
        <div className="border-t border-gray-200 bg-gray-50 p-5">
          {/* Header */}
          <div className="mb-4 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <span className="text-lg font-bold text-gray-900">
                {contract.number || `C${contract.id}`}
              </span>
              <Badge
                className={`${statusColor} pointer-events-none rounded-full px-2.5 py-0.5 text-xs font-medium`}
              >
                {contractStatus}
              </Badge>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">
                {contract.dateStarted && formatDate(contract.dateStarted)}
              </p>
              {contractAmount && (
                <p className="text-lg font-bold text-green-700">{contractAmount}</p>
              )}
            </div>
          </div>

          {/* 2-column detail grid */}
          <div className="mb-4 grid grid-cols-2 gap-x-8 gap-y-3 border-t border-gray-200 pt-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Contract Period
              </p>
              <p className="mt-0.5 text-sm text-gray-900">{contractPeriod}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Payment Method
              </p>
              <p className="mt-0.5 text-sm text-gray-900">
                {contract.paymentType || 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Payment Status
              </p>
              <p className="mt-0.5 text-sm text-gray-900">
                {contract.paymentStatus || 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Payment Terms
              </p>
              <p className="mt-0.5 text-sm text-gray-900">
                {contract.paymentTerms || 'Not specified'}
              </p>
            </div>
          </div>

          {/* Properties Included */}
          {contractProperties.length > 0 && (
            <div className="mb-4 border-t border-gray-200 pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Properties Included
              </p>
              <div className="space-y-1">
                {contractProperties.map((p) => (
                  <div
                    key={p.id}
                    className="rounded border border-gray-100 bg-white px-3 py-2 text-sm text-gray-700"
                  >
                    {p.address}, {p.city} {p.state} {p.zip}
                  </div>
                ))}
              </div>
            </div>
          )}

          {propertyMembership?.contractId === contract.id && (
            <MembershipAddonsList
              className="mb-4 border-t border-gray-200 pt-4"
              membershipId={propertyMembership.id}
              isCommercial={propertyMembership.isCommercial}
            />
          )}

          {/* Services Included */}
          {servicesIncluded.length > 0 && (
            <div className="mb-4 border-t border-gray-200 pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Services Included
              </p>
              <div className="space-y-1">
                {servicesIncluded.map((service, i) => (
                  <div
                    key={i}
                    className="rounded border border-gray-100 bg-white px-3 py-2 text-sm text-gray-700"
                  >
                    {service}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contract Notes */}
          <div className="mb-4 border-t border-gray-200 pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Contract Notes
            </p>
            <div className="rounded border border-gray-100 bg-white px-3 py-2 text-sm text-gray-700">
              {contract.notes || 'No notes'}
            </div>
          </div>

          {/* View full contract button */}
          <div className="border-t border-gray-200 pt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowEditContract(true)}
              className="inline-flex items-center gap-1"
            >
              View full contract <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* View Contract Dialog */}
      {contract && (
        <Dialog open={showEditContract} onOpenChange={setShowEditContract}>
          <DialogContent className="max-w-6xl overflow-y-auto bg-white" disableOutsideClick>
            <EditContractInfo
              userID={contract.userId}
              contractID={contract.id}
              onClose={() => setShowEditContract(false)}
              onSaveFinished={() => setShowEditContract(false)}
              readOnly={true}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
